/**
 * lib/roles.ts — L239 (Round 6 Wave A, 2026-09-03).
 *
 * Web mirror of the backend dealer-access tiers
 * (backend/src/modules/leads/dealer-access.ts). The /dealer-management route
 * gate, the sidebar row, and the Sr Sales Manager picker all read from HERE so
 * a role added on the backend is added in exactly one place on the web.
 *
 * 🔒 Keep these arrays VERBATIM with the backend sets (lower-case). Matching
 * is exact, never substring — `operations_manager` is NOT a dealer manager.
 */

export const DEALER_ADMIN_ROLES = ['owner', 'admin', 'super_admin'] as const;

export const DEALER_TRIAGE_ROLES = ['lead_triage_coordinator'] as const;

export const DEALER_MANAGER_ROLES = [
  'manager',
  'sales_manager',
  'sr_sales_manager',
  'manager_sales',
  'sales_head',
  'team_head',
  'lead_manager',
] as const;

/**
 * L251 — roles that reach the dealer screens ONLY by acting as somebody else.
 * A coordinator has no dealers of their own: `dealer-access.ts:82-95` gives
 * them the MANAGER tier by resolving `onBehalfOfUserId` through
 * `resolveDealerActingPerson`, and refuses with 403 when that field is absent.
 * Every dealer request from such a session must carry it.
 */
export const DEALER_ON_BEHALF_ROLES = ['coordinator'] as const;

/**
 * Every role that may open /dealer-management
 * (admin ∪ triage ∪ manager ∪ on-behalf).
 */
export const DEALER_PAGE_ROLES: string[] = [
  ...DEALER_ADMIN_ROLES,
  ...DEALER_TRIAGE_ROLES,
  ...DEALER_MANAGER_ROLES,
  ...DEALER_ON_BEHALF_ROLES,
];

/**
 * Who may open the dealer credit LEDGER (LOCKED SPEC L8): admin + Accounts see
 * every dealer, a dealer manager sees his own.
 *
 * 🔒 DELIBERATELY NOT `DEALER_PAGE_ROLES`. That set is who may open the dealer
 * DIRECTORY, and it includes `lead_triage_coordinator` and `coordinator` —
 * roles that per `feedback_ss_dealers_belong_to_managers_only` have no dealers
 * of their own, and that `dealer-credit-ledger.routes.ts` refuses money. A web
 * gate wider than the server's puts a Dealer Ledger row in their sidebar and
 * lands them on a page of 403s.
 *
 * 🔒 It lives HERE, beside the sets it is built from, and not in
 * financeRoutes.tsx, so the test that checks it does not have to pull the whole
 * route table (and every lazy page behind it) into a worker to read one array.
 */
export const DEALER_LEDGER_ROLES: string[] = [
  ...DEALER_ADMIN_ROLES,
  'accountant', 'accounts_executive', 'accounts_approver',
  ...DEALER_MANAGER_ROLES,
];

export type DealerTier = 'admin' | 'triage' | 'manager' | null;

/** Match backend role normalization before applying exact authorization sets. */
export function normalizeRoleName(role: string | null | undefined): string {
  return (role ?? '').trim().toLowerCase();
}

/** Which dealer tier a role lands in; null = no dealer-page access. */
export function dealerTierFor(role: string | null | undefined): DealerTier {
  const r = normalizeRoleName(role);
  if (!r) return null;
  if ((DEALER_ADMIN_ROLES as readonly string[]).includes(r)) return 'admin';
  if ((DEALER_TRIAGE_ROLES as readonly string[]).includes(r)) return 'triage';
  if ((DEALER_MANAGER_ROLES as readonly string[]).includes(r)) return 'manager';
  // 🔒 L251 — delegated, not owned. The tier is the manager's; the SCOPE comes
  // from the manager named in onBehalfOfUserId, never from this user.
  if ((DEALER_ON_BEHALF_ROLES as readonly string[]).includes(r)) return 'manager';
  return null;
}

/**
 * L251 — does this role reach dealers only by acting as their own manager?
 * True ⇒ every dealer request must carry `onBehalfOfUserId`, and the screen
 * must name the manager it is acting for.
 */
export function dealerActsOnBehalf(role: string | null | undefined): boolean {
  return (DEALER_ON_BEHALF_ROLES as readonly string[]).includes(normalizeRoleName(role));
}

/**
 * Manager-picker predicate (the "Sr Sales Manager" dropdown on
 * DealerManagementPage): a user whose role is a dealer-manager role OR an
 * admin role — admins/owners stay pickable, as they were under the old
 * substring hint. Triage is NOT a manager.
 */
export function isManagerRole(role: string | null | undefined): boolean {
  // 🔒 L251 — a coordinator now lands in the manager TIER, but must never be
  // offered as a dealer's Sr Sales Manager: they own no dealers. Tier is what
  // you may do; this predicate is who you are.
  if (dealerActsOnBehalf(role)) return false;
  const tier = dealerTierFor(role);
  return tier === 'manager' || tier === 'admin';
}

/** The /dealer-management route + sidebar gate, as a predicate. */
export function canOpenDealerPage(role: string | null | undefined): boolean {
  return dealerTierFor(role) !== null;
}
